import { ReactElement } from 'react';

import { VisitAndAppointmentPrescriptionOnlyView } from '@dreryk/egabinet-visit-frontend-api';

import { TranslatableText, Translate } from '@dreryk/react-common';
import { Dropdown } from '@dreryk/react-components';

import { useResourceBranchByHealthcareProviderType } from '~libs.shared.account';

import { prescriptionVisitsTranslationKeys } from '../../../../translations';

import { usePrescriptionVisitCancelCommand } from './usePrescriptionVisitCancelCommand';

const useVisitCancelOptionTranslationKey = (reopened: boolean, chronicDiseaseExists: boolean): TranslatableText => {
   const genericCancelTranslationKey = useResourceBranchByHealthcareProviderType(
      prescriptionVisitsTranslationKeys.cancellingIsNotPossibleInOrganization,
      prescriptionVisitsTranslationKeys.cancellingIsNotPossibleInPractice
   );
   if (reopened) {
      return prescriptionVisitsTranslationKeys.cannotCancelReopenedVisit;
   }

   if (chronicDiseaseExists) {
      return prescriptionVisitsTranslationKeys.cannotCancelVisitWhenChronicDiseaseExists;
   }

   return genericCancelTranslationKey;
};

type VisitCancelDropdownItemProps = {
   readonly visitId?: VisitAndAppointmentPrescriptionOnlyView['visitId'];
   readonly enabled: boolean;
   readonly reopened: boolean;
   readonly chronicDiseaseExists: boolean;
   readonly refetchPrescriptionVisits: VoidFunction;
   readonly label: TranslatableText;
};

export const VisitCancelDropdownItem = (props: VisitCancelDropdownItemProps): ReactElement => {
   const { executeAsync: cancelVisit } = usePrescriptionVisitCancelCommand({
      onSuccess: props.refetchPrescriptionVisits,
      onFault: props.refetchPrescriptionVisits,
   });
   const tooltipTranslationKey = useVisitCancelOptionTranslationKey(props.reopened, props.chronicDiseaseExists);

   const handleClick = (hide: VoidFunction): void => {
      cancelVisit({
         visitId: props.visitId!.asNumber(),
      });
      hide();
   };

   return (
      <Dropdown.Item
         key={'visitCancel'}
         testId={`visitCancelOption${props.visitId}`}
         onClick={handleClick}
         disabled={!props.enabled}
      >
         <Dropdown.Item.Tooltip
            testId={'cancellingIsNotPossibleIfDocumentsWereAdded'}
            content={tooltipTranslationKey}
            disabled={props.enabled}
         >
            <Translate>{props.label}</Translate>
         </Dropdown.Item.Tooltip>
      </Dropdown.Item>
   );
};
